import { useCallback, useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import type { ReportRow, ReportsListResponse, ReportType, ShopSummary } from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { actionError } from '../../components/actionError.ts';
import { useCompact } from '../../hooks/useCompact.ts';
import { PeriodFilter } from '../reports/PeriodFilter.tsx';
import { ReportFormDialog } from '../reports/ReportFormDialog.tsx';
import { ReportsTable } from '../reports/ReportsTable.tsx';

const EMPTY_LIST: ReportsListResponse = {
  page_info: { page: 1, limit: 10, total: 0 },
  data: [],
};

export interface ShopReportsSectionProps {
  shop: ShopSummary;
}

export function ShopReportsSection({ shop }: Readonly<ShopReportsSectionProps>): React.JSX.Element {
  const { api, hasPermission } = useAuth();
  const compact = useCompact();
  const canWrite = hasPermission('reports:write');

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | null>(now.getMonth() + 1);
  const [reportType, setReportType] = useState<ReportType>(shop.shop_type);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [list, setList] = useState<ReportsListResponse>(EMPTY_LIST);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ReportRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReportRow | null>(null);

  const effectiveType = canWrite ? reportType : undefined;

  // Request-sequence guard: a later filter change can resolve before an
  // earlier one (out-of-order responses), so the loader only ever applies
  // the result matching the MOST RECENT request it fired (same pattern as
  // ReportsPage).
  const listSeqRef = useRef(0);

  const loadList = useCallback(() => {
    const seq = ++listSeqRef.current;
    setListLoading(true);
    setListError(null);
    api.reports
      .list({
        shop_id: shop.id,
        year,
        month: month ?? undefined,
        report_type: effectiveType,
        page,
        limit,
      })
      .then((data) => {
        if (listSeqRef.current !== seq) return;
        setList(data);
      })
      .catch((e: unknown) => {
        if (listSeqRef.current !== seq) return;
        setListError(actionError(e));
      })
      .finally(() => {
        if (listSeqRef.current !== seq) return;
        setListLoading(false);
      });
  }, [api, shop.id, year, month, effectiveType, page, limit]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setReportType(shop.shop_type);
    setPage(1);
  }, [shop.shop_type]);

  function setYearAndResetPage(y: number) {
    setYear(y);
    setPage(1);
  }
  function setMonthAndResetPage(m: number | null) {
    setMonth(m);
    setPage(1);
  }
  function setReportTypeAndResetPage(t: ReportType) {
    setReportType(t);
    setPage(1);
  }

  function openAdd(): void {
    setEditingReport(null);
    setDialogOpen(true);
  }

  function openEdit(report: ReportRow): void {
    setEditingReport(report);
    setDialogOpen(true);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h6">Daily report ledger</Typography>
        {canWrite && <Button onClick={openAdd}>Add report</Button>}
      </Box>

      <PeriodFilter
        year={year}
        month={month}
        reportType={reportType}
        showReportType={canWrite}
        onYearChange={setYearAndResetPage}
        onMonthChange={setMonthAndResetPage}
        onReportTypeChange={setReportTypeAndResetPage}
      />

      <ReportsTable
        loading={listLoading}
        error={listError}
        rows={list.data}
        pageInfo={list.page_info}
        canWrite={canWrite}
        hideShopColumn
        hideTypeColumn
        compact={compact}
        onPageChange={setPage}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
      />

      {canWrite && (
        <ReportFormDialog
          open={dialogOpen}
          report={editingReport}
          shops={[shop]}
          defaultShopId={shop.id}
          lockShop
          defaultReportType={reportType}
          onClose={() => setDialogOpen(false)}
          onSaved={loadList}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete report"
        body={<Typography>Delete the report for {deleteTarget?.submission_date}? This cannot be undone.</Typography>}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={async () => {
          if (deleteTarget) await api.reports.remove(deleteTarget.id);
        }}
        onClose={() => setDeleteTarget(null)}
        onSuccess={loadList}
      />
    </Box>
  );
}

export default ShopReportsSection;
